/**
 * Incognito listening.
 *
 * Held in sessionStorage rather than localStorage, and that is the whole design
 * decision: a private session should end when the tab does, the same way a
 * browser's private window works. Surviving a restart would mean someone could
 * be in incognito days later without remembering they turned it on — and this
 * feature quietly deletes what you play, so being wrong about which mode you are
 * in is expensive in one direction.
 *
 * The server is the source of truth for what has been marked ephemeral; this
 * only decides whether new plays are flagged.
 */

import { create } from 'zustand'

const KEY = 'tunes.incognito'

function read(): boolean {
  try {
    return sessionStorage.getItem(KEY) === '1'
  } catch {
    // Safari in private mode throws on access rather than returning null.
    return false
  }
}

function write(active: boolean): void {
  try {
    if (active) sessionStorage.setItem(KEY, '1')
    else sessionStorage.removeItem(KEY)
  } catch {
    // Not being able to remember the choice is survivable; failing to set it is not.
  }
}

interface IncognitoState {
  active: boolean
  set: (active: boolean) => void
  toggle: () => void
}

export const useIncognito = create<IncognitoState>((set, get) => ({
  active: read(),
  set: (active) => {
    write(active)
    set({ active })
  },
  toggle: () => get().set(!get().active),
}))

/**
 * For non-React callers — the API layer needs this on every add-track request
 * and has no hook to read it from.
 */
export function isIncognito(): boolean {
  return useIncognito.getState().active
}
